import React, { JSX } from 'react';
import styled from 'styled-components';

import { ButtonVariant, FormInputVariants } from '#/service/constants';
import { remify } from '#/service/utils';

import * as Styled from './Form.styled';
import { FormGroupButtonProps } from './Form.types';
import Markdown from './FormInput/components/Markdown';

/** Disclaimer box for form buttons */
const Disclaimer = styled.div`
  background: var(--pageSecondaryBackground);
  padding: ${remify(12)};
  border-radius: ${remify(8)};
  display: flex;
  flex-direction: column;
  gap: ${remify(12)};
  text-align: center;

  /* trim outside margins from markdown content */
  & ${Styled.Markdown} {
    margin: ${remify(-3)} 0;
  }
`;

/** Conditionally wraps a form button in a disclaimer box */
export const FormButtonDisclaimer = ({
  disclaimer,
  children,
}): JSX.Element | null => {
  if (!disclaimer) {
    return children;
  }

  return (
    <Disclaimer>
      <Markdown linkTarget="_blank">{disclaimer}</Markdown>
      {children}
    </Disclaimer>
  );
};

export const FormGroupButton = ({
  activeButtonKey,
  button,
  handleSubmit,
  getFormData,
  isButtonDisabled,
}: FormGroupButtonProps) => {
  const { key, disclaimer, text, isMainButton } = button;
  const isActive = key === activeButtonKey;
  const isSubmit = button.variant === FormInputVariants.SUBMIT;
  const isPrimary = isSubmit || isMainButton;
  const isDisabled = isButtonDisabled(button);

  const getClickHandler =
    button.variant === FormInputVariants.SUBMIT
      ? (event: React.MouseEvent) => {
          handleSubmit(button.key, button.onClick);
          // submit buttons are rendered with `type="submit"`, but we handle the submission ourselves,
          // via `button.onClick`, so we need preventDefault to avoid double submission.
          event?.preventDefault();
        }
      : () => button.onClick(getFormData());

  return (
    <FormButtonDisclaimer disclaimer={disclaimer} key={key}>
      <Styled.FormButton
        data-testid="form-button"
        disabled={(isActive || isDisabled) && isPrimary}
        isLoading={isActive}
        onClick={getClickHandler}
        type={isSubmit ? 'submit' : 'button'}
        variant={isPrimary ? ButtonVariant.Primary : ButtonVariant.Secondary}
      >
        {text}
      </Styled.FormButton>
    </FormButtonDisclaimer>
  );
};
